import { AVATAR_COLORS, EMPTY_ANALYTICS } from '../constants/constants';
import type { ExcelColumn } from '../../../utils/excelExport';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { formatAuditDateTime, mapAuditFields } from '../../../lib/auditColumnDefs';
import { getPaymentRedirectStatusPagePath } from '../../../utils/apiPaths';
import { apiService } from '../../../services/apiService';
import { normalizeStatusKey, getTransactionModeLabel } from '../../../utils/status';
import type {
  OrderAnalytics,
  OrderFilter,
  OrderListRequest,
  OrderModeFilter,
  TransactionColumnFilterKey,
  TransactionColumnFilters,
  TransactionDccDetail,
} from '../models/types';

export function formatHeroDate() {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getMerchantInitial(name: string) {
  return (name?.trim()?.[0] ?? '?').toUpperCase();
}

export function formatPaymentMethodLabel(method?: string) {
  if (!method) return '—';
  return String(method)
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export function resolvePaymentDisplayType(paymentMethod?: string) {
  const normalized = String(paymentMethod ?? '').toUpperCase();
  if (normalized.includes('CRYPTO')) return 'Crypto';
  if (normalized.includes('BANK') || normalized.includes('TRANSFER')) return 'Bank';
  return 'Card';
}

export function mapApiOrder(t: Record<string, unknown>) {
  const rawStatus = String(t.status ?? '').trim();
  const paymentMethod = String(t.paymentMethod ?? '');

  return {
    id: String(t.orderId ?? t.id ?? ''),
    dbId: t.id,
    merchant: String(t.customerName ?? t.merchantName ?? 'Unknown'),
    merchantName: String(t.merchantName ?? '—'),
    merchantId: t.merchantId != null ? String(t.merchantId) : '',
    customerName: String(t.customerName ?? '—'),
    customerEmail: String(t.customerEmail ?? '—'),
    customerPhone: String(t.customerPhone ?? '—'),
    acquirerName: String(t.acquirerName ?? '—'),
    acquirerCode: String(t.acquirerCode ?? ''),
    acquirerTerminalName: String(t.acquirerTerminalName ?? '—'),
    merchantTerminalName: String(t.merchantTerminalName ?? '—'),
    paymentMethod,
    paymentMethodLabel: formatPaymentMethodLabel(paymentMethod),
    transactionId: String(t.transactionId ?? '—'),
    transactionStatus: String(t.transactionStatus ?? '—'),
    referenceNumber: String(t.referenceNumber ?? '—'),
    acquirerReference: String(t.acquirerReference ?? '—'),
    errorCode: String(t.errorCode ?? '—'),
    message: String(t.message ?? '—'),
    description: String(t.description ?? '—'),
    fee: t.fee != null ? Number(t.fee) : null,
    netAmount: t.netAmount != null ? Number(t.netAmount) : null,
    flowName: String(t.flowName ?? '—'),
    amount: Number(t.amount ?? 0),
    currency: String(t.currency ?? ''),
    // Super-admin only (API sends null otherwise): the amount actually sent to the acquirer.
    acquirerAmount: t.acquirerAmount != null ? Number(t.acquirerAmount) : null,
    acquirerCurrency: t.acquirerCurrency != null ? String(t.acquirerCurrency) : null,
    cardNumber: String(t.cardNumber ?? ''),
    cardType: String(t.cardType ?? ''),
    callerIp: t.callerIp ? String(t.callerIp) : '',
    trafficSourceUrl: t.trafficSourceUrl ? String(t.trafficSourceUrl) : '',
    registeredStoreUrls: Array.isArray(t.registeredStoreUrls)
      ? (t.registeredStoreUrls as unknown[]).map((u) => String(u)).filter(Boolean)
      : [],
    trafficSources: Array.isArray(t.trafficSources)
      ? (t.trafficSources as Array<{ type?: string; value?: string; observedAt?: string }>).map((s) => ({
          type: String(s.type ?? ''),
          value: String(s.value ?? ''),
          observedAt: s.observedAt ? String(s.observedAt) : '',
        }))
      : [],
    status: normalizeStatusKey(rawStatus) || 'pending',
    rawStatus,
    date: formatAuditDateTime(t.createdAt ? String(t.createdAt) : null),
    // Split parts for timeline/cards; grid "Txn Date" uses createdAt + formatAuditDateTime.
    dateOnly: t.createdAt
      ? new Date(String(t.createdAt)).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '—',
    timeOnly: t.createdAt
      ? new Date(String(t.createdAt)).toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—',
    reason: String(t.message ?? t.reason ?? ''),
    type: resolvePaymentDisplayType(paymentMethod),
    countryFlag: String(t.countryFlag ?? '🏳️'),
    country: String(t.countryName ?? 'Unknown'),
    countryId: t.countryId != null ? Number(t.countryId) : undefined,
    checkoutUrl: t.checkoutUrl ? String(t.checkoutUrl) : '',
    isCheckout: Boolean(t.isCheckout),
    testMode: Boolean(t.testMode),
    ...mapAuditFields(t),
  };
}

export type MappedOrder = ReturnType<typeof mapApiOrder>;

export function mapApiAnalytics(raw: Record<string, unknown> | null | undefined): OrderAnalytics {
  if (!raw) return EMPTY_ANALYTICS;

  const volumeTrend = Array.isArray(raw.volumeTrend)
    ? raw.volumeTrend.map((item: Record<string, unknown>) => {
        const totalCount = Number(item.totalCount ?? 0);
        const failedCount = Number(
          item.failedCount ?? item.flaggedCount ?? 0,
        );
        const pendingCount = Number(item.pendingCount ?? 0);
        const refundedCount = Number(item.refundedCount ?? 0);
        const completedCount = Number(
          item.completedCount ??
            item.successCount ??
            Math.max(0, totalCount - failedCount - pendingCount),
        );

        return {
          label: String(item.label ?? ''),
          date: String(item.date ?? ''),
          totalCount,
          completedCount,
          pendingCount,
          failedCount,
          refundedCount,
          volume: Number(item.volume ?? item.totalVolume ?? 0),
        };
      })
    : [];

  const paymentTypes = Array.isArray(raw.paymentTypes)
    ? raw.paymentTypes.map((item: Record<string, unknown>) => ({
        paymentMethod: String(item.paymentMethod ?? ''),
        label: String(item.label ?? item.paymentMethod ?? 'Unknown'),
        count: Number(item.count ?? 0),
        percentage: Number(item.percentage ?? 0),
      }))
    : [];

  const topCurrencies = Array.isArray(raw.topCurrencies)
    ? raw.topCurrencies.map((item: Record<string, unknown>) => ({
        currency: String(item.currency ?? item.label ?? ''),
        amount: Number(item.amount ?? item.volume ?? 0),
        percentage: Number(item.percentage ?? 0),
      }))
    : [];

  // Super-admin-only splits: the backend sends [] to every other caller, so a normal merchant
  // session simply yields empty arrays here rather than anything needing to be hidden.
  const acquirerSplit = Array.isArray(raw.acquirerSplit)
    ? raw.acquirerSplit.map((item: Record<string, unknown>) => ({
        name: String(item.name ?? 'Unknown'),
        acquirerName: item.acquirerName == null ? null : String(item.acquirerName),
        count: Number(item.count ?? 0),
        percentage: Number(item.percentage ?? 0),
      }))
    : [];

  const merchantSplit = Array.isArray(raw.merchantSplit)
    ? raw.merchantSplit.map((item: Record<string, unknown>) => ({
        name: String(item.name ?? 'Unknown'),
        amount: Number(item.amount ?? 0),
        percentage: Number(item.percentage ?? 0),
      }))
    : [];

  const terminalApprovalRates = Array.isArray(raw.terminalApprovalRates)
    ? raw.terminalApprovalRates.map((item: Record<string, unknown>) => ({
        name: String(item.name ?? 'Unknown'),
        acquirerName: item.acquirerName == null ? null : String(item.acquirerName),
        total: Number(item.total ?? 0),
        approved: Number(item.approved ?? 0),
        approvalRate: Number(item.approvalRate ?? 0),
      }))
    : [];

  const countryTransactions: Record<string, number> = {};
  if (raw.countryTransactions && typeof raw.countryTransactions === 'object') {
    Object.entries(raw.countryTransactions as Record<string, unknown>).forEach(
      ([country, count]) => {
        const n = Number(count);
        if (country && Number.isFinite(n) && n > 0) countryTransactions[country] = n;
      },
    );
  }

  const hourlyTrend: Record<string, number> = {};
  if (raw.hourlyTrend && typeof raw.hourlyTrend === 'object') {
    Object.entries(raw.hourlyTrend as Record<string, unknown>).forEach(([hour, count]) => {
      const n = Number(count);
      if (hour && Number.isFinite(n)) hourlyTrend[hour] = n;
    });
  }

  return {
    totalVolume: Number(raw.totalVolume ?? 0),
    weeklyTransactionCount: Number(raw.weeklyTransactionCount ?? 0),
    previousWeekTransactionCount: Number(raw.previousWeekTransactionCount ?? 0),
    transactionChangePercent: Number(raw.transactionChangePercent ?? 0),
    totalCompletedTransactions: Number(raw.totalCompletedTransactions ?? 0),
    totalFailedTransactions: Number(raw.totalFailedTransactions ?? 0),
    totalPendingTransactions: Number(raw.totalPendingTransactions ?? 0),
    totalRefundTransactions: Number(
      raw.totalRefundTransactions ?? raw.totalRefundedTransactions ?? 0,
    ),
    totalChargebackTransactions: Number(raw.totalChargebackTransactions ?? 0),
    totalOrdersCount: Number(raw.totalOrdersCount ?? 0),
    totalProcessingTransactions: Number(raw.totalProcessingTransactions ?? 0),
    totalAwaitingCheckoutTransactions: Number(raw.totalAwaitingCheckoutTransactions ?? 0),
    volumeTrend,
    paymentTypes,
    topCurrencies,
    countryTransactions,
    hourlyTrend,
    acquirerSplit,
    merchantSplit,
    terminalApprovalRates,
  };
}

export function formatChangePercent(value: number) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}% vs last week`;
}

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds `buildOrdersPayload` — so header-funnel filters and the
 * drawer both produce identical `request.filters` / `fromDate`/`toDate` / `mode` payloads.
 *
 * Text filters → `{ [colId]: [filterText] }` (drawer multi-select uses string[] too).
 * Date column (`agDateColumnFilter`) → `dateOnly: [from, to]` yyyy-MM-dd.
 * Mode column → dedicated `modeFilter` (Sandbox/Live), not a dynamicFilters entry — same as
 * the Sandbox/Live dropdown (`OrderListRequest.mode`).
 */
export function mapAgGridFilterModelToOrderFilters(
  filterModel: Record<string, unknown> | null | undefined,
): {
  columnFilters: Partial<TransactionColumnFilters>;
  modeFilter: OrderModeFilter | null;
} {
  const columnFilters: Partial<TransactionColumnFilters> = {};
  let modeFilter: OrderModeFilter | null = null;

  if (!filterModel) {
    return { columnFilters, modeFilter };
  }

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as {
      filter?: unknown;
      dateFrom?: string;
      dateTo?: string;
    };

    if (colId === 'dateOnly') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.dateOnly = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;

    if (colId === 'mode') {
      const text = model.filter.trim().toLowerCase();
      if ('sandbox'.startsWith(text)) modeFilter = 'sandbox';
      else if ('live'.startsWith(text)) modeFilter = 'live';
      continue;
    }

    if (colId in EMPTY_TRANSACTION_COLUMN_FILTER_KEYS) {
      columnFilters[colId as TransactionColumnFilterKey] = [model.filter];
    }
  }

  return { columnFilters, modeFilter };
}

const EMPTY_TRANSACTION_COLUMN_FILTER_KEYS: Record<TransactionColumnFilterKey, true> = {
  merchantName: true,
  transactionId: true,
  dateOnly: true,
  timeOnly: true,
  acquirerName: true,
  acquirerTerminalName: true,
  amount: true,
  currency: true,
  customerName: true,
  customerEmail: true,
  country: true,
  status: true,
  mode: true,
  merchantTerminalName: true,
  message: true,
};

export function buildOrdersPayload(options: {
  filter: OrderFilter;
  search: string;
  columnFilters: TransactionColumnFilters;
  page?: number;
  size?: number;
  export?: boolean;
  modeFilter?: OrderModeFilter;
}): OrderListRequest {
  const request: OrderListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  if (options.filter !== 'all') {
    request.orderStatus = options.filter.toUpperCase();
  }

  if (options.modeFilter && options.modeFilter !== 'all') {
    request.mode = options.modeFilter.toUpperCase() as 'SANDBOX' | 'LIVE';
  }

  const query = options.search.trim();
  if (query) request.search = query;

  // "Txn Date" is a dateRange field (GridColumnFilterPanel's DateRangePicker), stored as a
  // [from, to] pair of yyyy-MM-dd strings. Send it both ways so the payload matches other
  // list pages' date filters (`filters.dateOnly: [from, to]`, same shape as merchants/
  // acquirers `filters.createdAt`) while still satisfying the backend's dedicated
  // fromDate/toDate range query (OrderSpecifications.inDateRange).
  const dateRange = (options.columnFilters.dateOnly ?? []).filter(Boolean);
  const [dateFrom, dateTo] = options.columnFilters.dateOnly ?? [];
  if (dateFrom && dateTo) {
    request.fromDate = dateFrom;
    request.toDate = dateTo;
  }

  const filters = Object.fromEntries(
    (
      Object.entries(options.columnFilters) as [
        TransactionColumnFilterKey,
        string[] | null,
      ][]
    )
      .filter(([key, values]) => {
        if (key === 'dateOnly') return dateRange.length > 0;
        return Boolean(values?.length);
      })
      .map(([key, values]) =>
        key === 'dateOnly'
          ? [key, dateRange]
          : [key, values as string[]],
      ),
  );

  if (Object.keys(filters).length > 0) request.filters = filters;
  if (options.export) request.export = true;

  return request;
}

function getOrderExportColumns(isMerchantUser: boolean, isReferralUser = false): ExcelColumn[] {
  // Same columns and order as createTransactionColumnDefs (Action column excluded) — a referral
  // admin's grid hides Email, Acquirer/Acquirer Terminal/Terminal/DCC, so the export must match
  // exactly, not just fall back to the generic "any non-merchant admin" column set.
  const columns: ExcelColumn[] = [];

  if (!isMerchantUser) {
    columns.push({ header: 'Merchant', key: 'merchantName' });
  }

  columns.push(
    { header: 'Txn Id', key: 'transactionId' },
    { header: 'Txn Date', key: 'dateOnly' },
    { header: 'Time', key: 'timeOnly' },
  );

  if (!isMerchantUser && !isReferralUser) {
    columns.push(
      { header: 'Acquirer', key: 'acquirerName' },
      { header: 'Acquirer Terminal', key: 'acquirerTerminalName' },
    );
  }

  columns.push(
    { header: 'Amount', key: 'amount', numFmt: '#,##0.00' },
    { header: 'Currency', key: 'currency' },
    { header: 'Customer', key: 'customerName' },
  );

  if (!isReferralUser) {
    columns.push({ header: 'Email', key: 'customerEmail' });
  }

  columns.push(
    { header: 'Country', key: 'country' },
  );

  if (!isMerchantUser && !isReferralUser) {
    columns.push(
      { header: 'Terminal', key: 'merchantTerminalName' },
      { header: 'Base Currency', key: 'dccOriginalCurrency' },
      { header: 'Converted Currency', key: 'dccConvertedCurrency' },
      { header: 'Base Amount', key: 'dccOriginalAmount', numFmt: '#,##0.00' },
      { header: 'Converted Amount', key: 'dccConvertedAmount', numFmt: '#,##0.00' },
    );
  }

  columns.push(
    { header: 'Message', key: 'message' },
    { header: 'Status', key: 'status' },
    { header: 'Mode', key: 'mode' },
  );
  return columns;
}

function currenciesMatch(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a.trim().toUpperCase() === b.trim().toUpperCase();
}

/** Same fallback as the grid DCC columns: no conversion means base === converted. */
function fallbackDccDetail(row: MappedOrder): TransactionDccDetail {
  return {
    transactionId: row.transactionId,
    dccApplied: false,
    originalCurrency: row.currency,
    convertedCurrency: row.currency,
    originalAmount: row.amount,
    convertedAmount: row.amount,
  };
}

function resolveDccDetail(
  realDcc: TransactionDccDetail | undefined,
  row: MappedOrder,
): TransactionDccDetail {
  if (realDcc && currenciesMatch(realDcc.settlementCurrency, row.currency)) {
    return fallbackDccDetail(row);
  }
  return realDcc?.dccApplied ? realDcc : fallbackDccDetail(row);
}

function resolveExportDccFields(
  row: MappedOrder,
  dccByTransactionId: Record<string, TransactionDccDetail>,
) {
  const transactionId = resolveRowTransactionId(row);
  const realDcc = transactionId ? dccByTransactionId[transactionId] : undefined;
  const dcc = resolveDccDetail(realDcc, row);

  return {
    dccOriginalCurrency: dcc.originalCurrency || '—',
    dccConvertedCurrency: dcc.convertedCurrency || '—',
    dccOriginalAmount: Number(dcc.originalAmount),
    dccConvertedAmount: Number(dcc.convertedAmount),
  };
}

export async function exportOrdersExcel(
  rows: MappedOrder[],
  filter: OrderFilter,
  isMerchantUser: boolean,
  dccByTransactionId: Record<string, TransactionDccDetail> = {},
  isReferralUser = false,
) {
  const includeAdminOnlyFields = !isMerchantUser && !isReferralUser;
  await exportRowsToExcel(
    rows.map((row) => ({
      merchantName: row.merchantName,
      transactionId: row.transactionId,
      dateOnly: row.dateOnly && row.dateOnly !== '—'
        ? row.dateOnly
        : formatAuditDateTime(row.createdAt),
      timeOnly: row.timeOnly && row.timeOnly !== '—' ? row.timeOnly : '—',
      acquirerName: row.acquirerName,
      acquirerTerminalName: row.acquirerTerminalName,
      amount: row.amount,
      currency: row.currency,
      customerName: row.customerName,
      customerEmail: row.customerEmail,
      country: row.country,
      status: row.rawStatus || row.status,
      mode: getTransactionModeLabel(row.testMode),
      merchantTerminalName: row.merchantTerminalName,
      message: row.message && row.message !== '—' ? row.message : '—',
      ...(includeAdminOnlyFields ? resolveExportDccFields(row, dccByTransactionId) : {}),
    })),
    getOrderExportColumns(isMerchantUser, isReferralUser),
    { sheetName: 'Orders', fileNamePrefix: `orders-${filter}` },
  );
}

export function resolveRowTransactionId(row: MappedOrder): string | null {
  const txnId = row.transactionId?.trim();
  if (txnId && txnId !== '—') return txnId;
  return null;
}

// Only super admins reach this (the action is hidden for merchants and referral admins). A finished
// transaction's status/redirect flow is blocked server-side for everyone else, so we first mint a
// short-lived, super-admin-only replay token and carry it in the URL (?h=) — without it even a super
// admin would see "This transaction is already finished." on a completed/declined transaction.
export async function openTransactionStatusPage(transactionId: string) {
  // EXACTLY the pre-change flow — open the normal status page. The only addition is the super-admin
  // replay token in ?h=, which lets the super admin through the finished-transaction guard so the
  // status page loads and behaves identically to before. Anyone without the token (the acquirer
  // replaying, a leaked link) is blocked once the transaction is finished.
  let hash = '';
  try {
    const res = await apiService.get<{ token?: string }>(
      `/payments/redirect/replay-token/${encodeURIComponent(transactionId)}`,
    );
    hash = res?.token ?? '';
  } catch {
    // Not a super admin, or the mint failed — open without a token.
  }
  const base = getPaymentRedirectStatusPagePath(transactionId);
  const url = hash ? `${base}?h=${encodeURIComponent(hash)}` : base;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function resolveHeatmapType(paymentMethod?: string) {
  const type = resolvePaymentDisplayType(paymentMethod);
  if (type === 'Bank') return 'Transfer';
  if (type === 'Crypto') return 'Crypto';
  return 'Card';
}





